// surface.ts — INFRA DE BIFURCAÇÃO de plataforma (parede A0 do mobile).
//
// O mesmo source-code do app roda em TV (Tizen/webOS/Android TV/Fire/VIDAA) e,
// a partir da frente mobile, também em celular/tablet via navegador. Esta função
// decide UMA VEZ qual SUPERFÍCIE renderizar: 'tv' (caminho de hoje, intacto) ou
// 'mobile' (telas touch novas). O RootRoute bifurca no topo com base nisso.
//
// PRINCÍPIO CONSERVADOR: na dúvida, 'tv'. A sala é o mercado primário e não pode
// regredir. Classificamos como 'mobile' quando o viewport é ESTREITO (<768),
// com ou sem touch, OU quando a UA já é mobile (mobile-pwa). Qualquer UA de TV é
// hard override pra 'tv' ANTES de tudo, mesmo que a TV reporte viewport pequeno
// ou touch (algumas TVs/emuladores mentem). Como TV tem viewport SEMPRE largo
// (1280/1920) e é pega pelo hard override de UA, a regra de largura estreita
// jamais classifica uma TV como mobile — ver passo 3.

import { detectPlatform } from '@app-iptv/device'

export type Surface = 'tv' | 'mobile'

/** UA-platforms que são SEMPRE TV (hard override). detectPlatform() já classifica
 *  pelo UA; estas nunca viram mobile. */
const TV_PLATFORMS = new Set(['tizen', 'webos', 'androidtv', 'firetv', 'vidaa'])

// ===== BYPASS DE TESTE — override manual de superfície (QA/dev) =====
// O Fernando precisa testar TV e mobile em QUALQUER largura/device sem depender do
// DevTools (que injeta UA mobile e força mobile em tela larga). Solução: um override
// explícito via query param `?surface=tv` ou `?surface=mobile`.
//
// COMO FUNCIONA:
//  - `?surface=tv` → força 'tv'; `?surface=mobile` → força 'mobile'.
//  - O valor é persistido em sessionStorage (`iptv:surface-override`) pra sobreviver
//    à navegação interna (SPA) e a reloads NA MESMA ABA, sem repetir o param.
//  - `?surface=auto` (ou qualquer valor inválido) LIMPA o override e volta à
//    detecção normal.
//  - Só 'tv' e 'mobile' são aceitos; qualquer outra coisa = ignorado.
//
// PRECEDÊNCIA: este override vence TUDO, inclusive o hard override de UA de TV — é
// ferramenta de teste explícita (se eu peço ?surface=mobile, quero mobile mesmo numa
// TV de teste).
//
// NÃO AFETA PRODUÇÃO: numa TV real (ou celular real) ninguém passa `?surface=` na
// URL nem tem nada salvo no sessionStorage daquela aba, então `readSurfaceOverride()`
// devolve null e a detecção normal segue 100% intacta. É infra de QA/dev, não fluxo
// de usuário.
const SURFACE_OVERRIDE_STORAGE_KEY = 'iptv:surface-override'

function isSurface(value: string | null | undefined): value is Surface {
  return value === 'tv' || value === 'mobile'
}

/**
 * Lê o override de teste de superfície. Ordem:
 *  1. Se há `?surface=` na URL, ele MANDA:
 *     - 'tv' | 'mobile' → grava no sessionStorage e retorna o valor.
 *     - 'auto' / inválido → limpa o sessionStorage e retorna null (detecção normal).
 *  2. Sem param na URL, lê o que estiver no sessionStorage (persistência da aba).
 *  3. Sem nada → null (sem override; segue detecção normal).
 *
 * Guards de SSR/teste-node: se não há `window`, não há como ter override → null.
 * Acessos a URLSearchParams/sessionStorage são protegidos por try/catch (sandbox,
 * sessionStorage desabilitado em modo privado de alguns browsers, etc.).
 */
function readSurfaceOverride(): Surface | null {
  if (typeof window === 'undefined') return null

  // 1. Query param vence: aplica/atualiza/limpa o override persistido.
  try {
    const param = new URLSearchParams(window.location.search).get('surface')
    if (param !== null) {
      if (isSurface(param)) {
        try {
          window.sessionStorage?.setItem(SURFACE_OVERRIDE_STORAGE_KEY, param)
        } catch {
          // sessionStorage indisponível (modo privado/sandbox): segue só com o
          // valor do param nesta resolução.
        }
        return param
      }
      // 'auto' ou valor inválido → limpa o override e cai na detecção normal.
      try {
        window.sessionStorage?.removeItem(SURFACE_OVERRIDE_STORAGE_KEY)
      } catch {
        // ignora — sessionStorage indisponível.
      }
      return null
    }
  } catch {
    // window.location ou URLSearchParams indisponível: ignora o param.
  }

  // 2. Sem param na URL: usa o override persistido na aba (navegação SPA/reload).
  try {
    const stored = window.sessionStorage?.getItem(SURFACE_OVERRIDE_STORAGE_KEY)
    if (isSurface(stored)) return stored
  } catch {
    // ignora — sessionStorage indisponível.
  }

  // 3. Sem override.
  return null
}

/**
 * Override de teste ATIVO nesta aba (`?surface=tv|mobile`), ou `null` quando a
 * detecção é automática.
 *
 * Exposto porque o modo de TECLADO (ver `@app-iptv/ui` keyboard-mode) não pode
 * sair de `resolveSurface()` cru: um browser de desktop largo resolve 'tv'
 * (renderiza a UI de TV) mas NÃO tem IME modal — lá o campo deve focar e abrir
 * o teclado normalmente, como sempre. Já quando o Fernando força `?surface=tv`
 * pra testar a TV no PC, ele quer o modelo de TV inteiro (OK abre o teclado).
 * Só o override EXPLÍCITO justifica mudar a capability; a detecção normal fica
 * por conta da plataforma real.
 */
export function getSurfaceOverride(): Surface | null {
  return readSurfaceOverride()
}

/** Largura (CSS px) abaixo da qual consideramos "tela estreita" de celular/tablet
 *  em portrait. 768 é o breakpoint clássico tablet/phone. Viewport estreito basta
 *  pra virar 'mobile' (com ou sem touch): cobre DevTools responsivo no PC, desktop
 *  encapsulado em janela pequena e 2-em-1. Tablet landscape largo (>768) cai em
 *  'mobile' pela UA mobile-pwa (passo 4). */
const NARROW_MAX_WIDTH = 768

/**
 * Decisão PURA de superfície a partir do ambiente atual (sem cache).
 * Exportada separadamente pra ser testável determinística — `resolveSurface()`
 * (com cache de sessão) é o que o app consome.
 *
 * Lógica (passo 0 de teste + 4 linhas conceituais):
 *  0. OVERRIDE DE TESTE (QA/dev): `?surface=tv|mobile` (ou persistido na aba via
 *     sessionStorage) vence TUDO. Ausente em produção → null → segue detecção normal.
 *  1. UA de TV (TV_PLATFORMS) → 'tv' SEMPRE (hard override conservador).
 *  2. SSR / sem window → 'tv' (boot seguro; servidor não é celular).
 *  3. viewport estreito (<768), COM OU SEM touch → 'mobile'. Cobre celular,
 *     DevTools responsivo, janela desktop pequena e 2-em-1.
 *  4. plataforma já classificada como mobile-pwa pelo UA → 'mobile'; senão 'tv'.
 *
 * Tablet cai em 'mobile' (layout intermediário vem em parede posterior): ou pelo
 * passo 3 (portrait estreito) ou pelo passo 4 (UA mobile-pwa, ex: landscape largo).
 *
 * POR QUE A TV NÃO REGRIDE com a regra de largura: TVs têm viewport LARGO
 * (1280/1920), nunca <768, e o hard override de UA (passo 1) pega
 * Tizen/webOS/Android TV/Fire/VIDAA antes de qualquer cálculo de largura. Logo
 * `isNarrow` jamais classifica uma TV como 'mobile'.
 */
export function computeSurface(): Surface {
  // 0. OVERRIDE DE TESTE (QA/dev) — vence TUDO, inclusive o hard override de UA de
  //    TV abaixo. Só existe quando `?surface=` foi passado nesta aba (ou persistido
  //    nela). Em device real ninguém passa o param → null → detecção normal intacta.
  const override = readSurfaceOverride()
  if (override !== null) return override

  const platform = detectPlatform()

  // 1. Hard override TV — UA de TV nunca é mobile.
  if (TV_PLATFORMS.has(platform)) return 'tv'

  // 2. Sem ambiente de browser (SSR/teste node) → TV conservador.
  if (typeof window === 'undefined') return 'tv'

  // 3. Viewport ESTREITO (<768) → mobile, com ou sem touch. Cobre celular,
  //    DevTools responsivo no PC, janela desktop pequena e 2-em-1. TV nunca cai
  //    aqui: tem viewport largo e já foi pega pelo hard override do passo 1.
  const width =
    typeof window.innerWidth === 'number' && window.innerWidth > 0
      ? window.innerWidth
      : Number.POSITIVE_INFINITY
  const isNarrow = width < NARROW_MAX_WIDTH
  if (isNarrow) return 'mobile'

  // 4. UA já diz mobile-pwa (smartphone/tablet com viewport largo — ex: tablet
  //    landscape) → mobile. Desktop/web largo → tv.
  if (platform === 'mobile-pwa') return 'mobile'

  return 'tv'
}

// ===== Surface estável por sessão =====
// Decidimos UMA VEZ no boot e cacheamos. Resize/rotação no meio da sessão NÃO
// trocam a superfície — trocar remontaria a árvore inteira (perde estado de
// player, scroll, navegação) e causaria flicker caótico. Quem quiser reagir a
// resize usa media queries dentro da superfície já escolhida, não troca de
// superfície. Pra testes, `__resetSurfaceCache()` limpa o memo.
let cached: Surface | null = null

export function resolveSurface(): Surface {
  // BYPASS DE TESTE: se há um override ativo (param na URL ou persistido na aba),
  // ele tem prioridade sobre o memo de sessão — assim trocar `?surface=` entre
  // carregamentos passa a valer na hora, sem ficar preso numa resolução anterior.
  // Em produção (sem override) `readSurfaceOverride()` é null e o memo normal manda.
  const override = readSurfaceOverride()
  if (override !== null) {
    cached = override
    return override
  }

  if (cached === null) cached = computeSurface()
  return cached
}

/** Apenas para testes — reseta o memo de sessão. Não usar em produção. */
export function __resetSurfaceCache(): void {
  cached = null
}
